/**
 * Hook for managing current user's privileges
 */

import { useState, useEffect, useCallback } from "react";
import { getCurrentUserPrivileges } from "../services/permissionsService";
import { useAppStore } from "../stores/store";

interface UseUserPrivilegesResult {
  /** All privileges for the current user */
  privileges: string[];
  /** Whether privileges are still loading */
  loading: boolean;
  /** Refetch privileges */
  refetch: () => Promise<void>;
}

/**
 * Module-level in-flight promise. Shared across every `useUserPrivileges`
 * caller (SessionBootstrap + per-page calls) and across React StrictMode
 * double-mounts, so we never issue more than one concurrent
 * `GET /authz/privileges` request even when many hook instances mount in
 * the same tick.
 */
let inFlightPrivilegesFetch: Promise<void> | null = null;

function loadPrivilegesOnce(
  setPrivileges: (data: string[]) => void,
): Promise<void> {
  if (inFlightPrivilegesFetch) return inFlightPrivilegesFetch;

  inFlightPrivilegesFetch = (async () => {
    try {
      const data = await getCurrentUserPrivileges();
      setPrivileges(data);
    } catch (error) {
      console.error("Error in useUserPrivileges:", error);
      setPrivileges([]);
    } finally {
      inFlightPrivilegesFetch = null;
    }
  })();

  return inFlightPrivilegesFetch;
}

export function useUserPrivileges(): UseUserPrivilegesResult {
  const [loading, setLoading] = useState(false);
  const privileges = useAppStore((state) => state.ui?.privileges) || [];
  const privilegesLoaded = useAppStore((state) => state.ui?.privilegesLoaded) || false;
  const setPrivileges = useAppStore((state) => state.setPrivileges);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      await loadPrivilegesOnce(setPrivileges);
    } finally {
      setLoading(false);
    }
  }, [setPrivileges]);

  // Initial load if not loaded yet (and not already in flight). Both checks
  // matter: `privilegesLoaded` covers "already done", `inFlightPrivilegesFetch`
  // covers the concurrent-mount window before that flag flips.
  useEffect(() => {
    if (privilegesLoaded || inFlightPrivilegesFetch) return;
    void refetch();
  }, [privilegesLoaded, refetch]);

  return { privileges, loading, refetch };
}
